import { useSession } from "next-auth/react";
import { useRouter } from "next/router";
import { getTimeStamp, slugify } from "../../lib/helpers";
import { Size } from "../../models/enums";
import { Artist } from "../../models/models";
import BlackButton from "../buttons/black-button";
import Icon from "../icons/icon";
import { useEffect, useState } from "react";
import SafeImage from "../safe-image";
import { isNil } from "lodash";
import Edit from "../icons/lib/edit";
import User from "../icons/lib/user";
import { cx } from "@/lib/utils/className.utils";
import BaseSkeleton from "../base-skeleton";

import { CREATORS_SLUG_ID } from "@/constants/routes";

interface ArtistArtProps {
  id?: string;
  artist: Artist;
  size?: Size;
  onClick?: Function;
  noHover?: boolean;
  children?: JSX.Element;
  edit?: boolean;
  onEdit?: Function;
  isLoading?: boolean;
  className?: string;
  imageClassName?: string;
}

const ArtistArt = ({
  id,
  artist,
  size,
  onClick,
  noHover,
  children,
  edit,
  onEdit,
  isLoading = false,
  className,
  imageClassName,
}: ArtistArtProps) => {
  // hooks
  const session = useSession();
  const router = useRouter();

  const [src, setSrc] = useState<string | null>(artist?.profilePicUrl);

  useEffect(() => {
    if (artist?.profilePicUrl?.includes("https:")) {
      setSrc(artist?.profilePicUrl);
    } else {
      setSrc(artist?.profilePicUrl ?? "");
    }
  }, [artist]);

  // actions
  const getSize = () => {
    switch (size) {
      case "xs":
        return "rounded-lg";
      case "sm":
        return "rounded-xl";
      case "md":
        return "rounded-2xl";
      default:
        return "rounded-full";
    }
  };

  const redirect = () => {
    if (session.data?.user.artistId !== artist.id) {
      router.push({
        pathname: CREATORS_SLUG_ID,
        query: {
          slug: slugify(artist.name),
          id: artist?.id
        }
      })
    }
  };

  if (isLoading) {
    return (
      <BaseSkeleton
        className={cx(
          "relative flex overflow-hidden cursor-pointer aspect-square h-full w-full rounded-full ${getSize()}",
          className
        )}
      />
    );
  }

  return (
    <div
      id={id}
      onClick={() =>
        !isNil(onClick) ? onClick?.() : !edit ? redirect() : onEdit && onEdit()
      }
      className={`relative flex overflow-hidden cursor-pointer shadow-[0_0_15px_5px] shadow-[#000000]/[0.1] bg-white aspect-square h-full w-full ${getSize()} ${
        className ?? ""
      }`}
    >
      <SafeImage
        width={size === "xs" ? 400 : 500}
        height={size === "xs" ? 400 : 500}
        src={src}
        className={cx(`object-cover z-0 h-full w-full`, imageClassName)}
        alt="artist-image"
        placeholder="blur"
        blurDataURL={"/assets/images/placeholder.png"}
        defaultIcon={
          <div
            className={cx(
              "flex justify-center items-center h-full w-full bg-gradient-to-br from-purple to-green p-[25%]",
              imageClassName
            )}
          >
            <Icon icon={User} color="white" className="w-full h-full" />
          </div>
        }
        priority
      />

      {!noHover && (
        <div className="absolute inset-0 flex transition-opacity duration-300 bg-black opacity-0 bg-opacity-60 hover:opacity-100">
          <div className="absolute inset-0 flex items-center justify-center">
            <BlackButton
              text="View Profile"
              onClick={() => null}
              className="py-3 px-7"
            />
          </div>
        </div>
      )}
      {edit && noHover && (
        <div className="absolute inset-0 flex transition-opacity duration-300 bg-black opacity-0 bg-opacity-60 hover:opacity-100">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex items-center gap-2">
              <Icon
                icon={Edit}
                color="white"
                className="h-[20px] w-[20px] mb-1"
              />
              <p className="text-white">Edit</p>
            </div>
          </div>
        </div>
      )}
      {children}
    </div>
  );
};

export default ArtistArt;
